import Entity from '../../../core/domain/entity';
import UniqueEntityID from '../../../core/domain/unique_entity_id';
import { Patient } from '../../patient/domain/patient';
import { PredefinedTask } from './predefined-task';
import { Task } from './task';

export type ToDoStatus = 'INITIAL' | 'ON_HOLD' | 'TERMINATED' | 'CANCELLED';

export type TaskEntityType = 'PATIENT' | 'DOCUMENT' | 'NOTE' | 'SURVEY';

export interface TaskTrigger {
  entityType: TaskEntityType;
  entityId: string;
}

export type TaskActionStatus = 'EXECUTED' | 'ON_HOLD' | 'CANCELLED';

export interface TaskAction {
  taskActionType: string;
  date: Date | undefined;
  status: TaskActionStatus;
}

export interface ToDoProps {
  dietitianId?: string;
  task: Task | PredefinedTask;
  taskTitle: string | undefined;
  dateStart: Date;
  dateEnd: Date | undefined;
  description: string;
  target: Patient | Patient[] | undefined;
  triggeredBy: TaskTrigger | undefined;
  actions: TaskAction[] | undefined;
  status: ToDoStatus;
  createdAt: Date;
  updatedAt: Date | undefined;
}

export class ToDoId extends Entity<unknown> {
  private constructor(id?: UniqueEntityID) {
    super(null, id);
  }

  public static create(id?: UniqueEntityID): ToDoId {
    return new ToDoId(id);
  }
}

export class ToDo extends Entity<ToDoProps> {
  get toDoId(): ToDoId {
    return ToDoId.create(this.id);
  }

  get dietitianId(): string | undefined {
    return this.props.dietitianId;
  }

  get task(): Task | PredefinedTask {
    return this.props.task;
  }

  set task(task: Task | PredefinedTask) {
    this.props.task = task;
  }

  get taskTitle(): string | undefined {
    return this.props.taskTitle;
  }

  set taskTitle(title: string | undefined) {
    this.props.taskTitle = title;
  }

  get dateStart(): Date {
    return this.props.dateStart;
  }

  set dateStart(date: Date) {
    this.props.dateStart = date;
  }

  get dateEnd(): Date | undefined {
    return this.props.dateEnd;
  }

  set dateEnd(date: Date | undefined) {
    this.props.dateEnd = date;
  }

  get description(): string {
    return this.props.description;
  }

  set description(description: string) {
    this.props.description = description;
  }

  get target(): Patient | Patient[] | undefined {
    return this.props.target;
  }

  set target(target: Patient | Patient[] | undefined) {
    this.props.target = target;
  }

  get triggeredBy(): TaskTrigger | undefined {
    return this.props.triggeredBy;
  }

  get actions(): TaskAction[] | undefined {
    return this.props.actions;
  }

  get status(): ToDoStatus {
    return this.props.status;
  }

  set status(status: ToDoStatus) {
    this.props.status = status;
  }

  get createdAt(): Date {
    return this.props.createdAt;
  }

  get updatedAt(): Date | undefined {
    return this.props.updatedAt;
  }

  set updatedAt(date: Date | undefined) {
    this.props.updatedAt = date;
  }

  private constructor(props: ToDoProps, id?: UniqueEntityID) {
    super(props, id);
  }

  copyWith(props: ToDoProps): ToDo {
    return ToDo.create(
      {
        ...this.props,
        ...props,
      },
      this.id,
    );
  }

  public static create(props: ToDoProps, id?: UniqueEntityID): ToDo {
    return new ToDo(props, id);
  }
}
